import { useState } from "react";
import type { TFunction } from "i18next";
import { Plus, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { FormActions } from "@jbnl/shared-ui";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Field,
  FieldDescription,
  FieldLabel,
  FieldLegend,
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import type { GeocodeTreffer } from "@/features/reistijden/useReistijden";
import { KorpsKiezer } from "./KorpsKiezer";
import { MeldadresZoeker } from "./MeldadresZoeker";
import { WedstrijdleiderPicker } from "./WedstrijdleiderPicker";
import { buitenSeizoen } from "./datum";
import {
  useKlassen,
  useNiveaus,
  type Korps,
  type Meldadres,
  type Seizoen,
  type Wedstrijddag,
} from "./useWedstrijden";

export type DagWaarden = {
  date: string;
  niveauId: string;
  organiserendKorpsId: string | null;
  meldadres: Meldadres | null;

  /** Create path only; the edit path drops these, since each has a card of its own (ADR-0030). */
  klasseIds: string[];
  wedstrijdleider: string | null;
  telefoonnummer: string | null;
};

const leegMeldadres: Meldadres = { address: "", postalCode: "", city: "" };

/**
 * Every edit drops the point: coordinates left under a changed address is the silent failure
 * ADR-0013 names. An unconfirmed address shows as one; a stale point does not.
 */
function metAdres(meldadres: Meldadres, veld: Partial<Meldadres>): Meldadres {
  return { ...meldadres, ...veld, latitude: null, longitude: null };
}

/**
 * Drops case, spacing and a leading "Kazerne", or fourteen of the sixteen 2026 meldadressen read
 * as overrides of the korps they belong to. Kept in step with `PdokGeocoder.Normaliseer` by hand.
 */
function genormaliseerd(waarde: string | null | undefined) {
  const zonderKazerne = (waarde ?? "")
    .trim()
    .replace(/^(?:brandweer)?kazerne\s+/i, "");

  // "Kazerne Bilthoven, Leyenseweg 40" - the kazerne's own name, where it has one. A segment
  // carrying no digit cannot be the street part, which always holds a huisnummer.
  const komma = zonderKazerne.indexOf(",");
  const zonderNaam =
    komma >= 0 && !/\d/.test(zonderKazerne.slice(0, komma))
      ? zonderKazerne.slice(komma + 1)
      : zonderKazerne;

  return zonderNaam.trim().replace(/\s+/g, " ").toLowerCase();
}

/** Whether the korps has a full address of its own that the meldadres does not already name. */
function overneembaarAdres(
  korps: Korps | undefined,
  meldadres: Meldadres,
): korps is Korps {
  return Boolean(
    korps?.address && korps.postalCode && !isAdresVan(meldadres, korps),
  );
}

/** The chosen waarde, falling back to the first option once the options have loaded. */
function metEersteAlsStandaard(
  waarde: string,
  opties: { id: string }[] | undefined,
) {
  return waarde || opties?.[0]?.id || "";
}

/** Whether the meldadres carries anything at all, as opposed to three empty strings. */
function heeftInhoud(meldadres: Meldadres) {
  return Boolean(meldadres.address || meldadres.postalCode || meldadres.city);
}

/** PDOK's fields translated to a Meldadres; a treffer only ever supplies what PDOK returned. */
function naarMeldadres(treffer: GeocodeTreffer): Meldadres {
  return {
    address: treffer.address ?? "",
    postalCode: treffer.postalCode ?? "",
    city: treffer.city ?? "",
    latitude: treffer.latitude,
    longitude: treffer.longitude,
  };
}

/**
 * Null is the whole of "neem het adres van het organiserend korps over", so an untouched
 * meldadres has to arrive as null rather than as three empty strings. With no korps at all it is
 * null for the other reason - there is no address to publish and none to take one from - and the
 * server refuses anything else. An empty boekje box is not an answer either, and the server
 * stores null for one regardless - sending "" would only differ from null in the request log.
 */
function naarDagWaarden(velden: {
  date: string;
  niveau: string;
  korpsId: string;
  ingevuld: boolean;
  meldadres: Meldadres;
  klasseIds: string[];
  wedstrijdleider: string;
  telefoonnummer: string;
}): DagWaarden {
  return {
    date: velden.date,
    niveauId: velden.niveau,
    organiserendKorpsId: velden.korpsId || null,
    meldadres: velden.korpsId && velden.ingevuld ? velden.meldadres : null,
    klasseIds: velden.klasseIds,
    wedstrijdleider: velden.wedstrijdleider.trim() || null,
    telefoonnummer: velden.telefoonnummer.trim() || null,
  };
}

/**
 * Only the edit path requires the meldadres, where an empty one means emptying a published
 * address; the banen and wedstrijdleiders are create-only (ADR-0030). The caller resets with a
 * `key`, not an effect, which would fire on a refetch and discard half-typed input.
 */
export function WedstrijddagForm({
  id,
  seizoen,
  korpsen,
  dag,
  submitLabel,
  pending,
  error,
  onSubmit,
}: {
  id: string;
  seizoen: Seizoen;
  korpsen: Korps[];
  dag?: Wedstrijddag;
  submitLabel: string;
  pending: boolean;
  error: Error | null;
  onSubmit: (waarden: DagWaarden) => void;
}) {
  const { t } = useTranslation(["wedstrijden", "common"]);
  const { data: niveaus } = useNiveaus();
  const { data: klassen } = useKlassen();

  /** Whether this is the create path, which is the one that asks for banen and wedstrijdleiders. */
  const nieuw = !dag;

  const [date, setDate] = useState(dag?.date ?? "");
  const [niveauId, setNiveauId] = useState(dag?.niveauId ?? "");
  const [korpsId, setKorpsId] = useState(dag?.organiserendKorpsId ?? "");
  const [meldadres, setMeldadres] = useState<Meldadres>(
    dag?.meldadres ?? leegMeldadres,
  );

  /**
   * Repeats are the point: two Aspiranten LD banen is the klasse named twice (#60). No ordinals,
   * because only the server hands those out and a guess here is contradicted on the next screen.
   */
  const [klasseIds, setKlasseIds] = useState<string[]>([]);
  const [teVoegen, setTeVoegen] = useState("");
  const [wedstrijdleider, setWedstrijdleider] = useState("");
  const [telefoonnummer, setTelefoonnummer] = useState("");

  /**
   * Separates a borrowed terrein from an address still to be looked up. `korpsAdres` answers a
   * different question, and a meldadres can fail both (ADR-0060).
   */
  const [handmatig, setHandmatig] = useState(false);

  /** The korps a confirmation is currently open about, or null when none is. */
  const [teBevestigen, setTeBevestigen] = useState<Korps | null>(null);

  const niveau = metEersteAlsStandaard(niveauId, niveaus);
  const klasse = metEersteAlsStandaard(teVoegen, klassen);
  const ingevuld = heeftInhoud(meldadres);
  const waarschuwing =
    date && buitenSeizoen(date, seizoen.startDate, seizoen.endDate);

  /** Where a meldadres without a point is routed from, which is the one thing worth saying. */
  const gekozenKorps = korpsen.find((k) => k.id === korpsId);

  /** Whether the meldadres overrides nothing, because it names the organiserend korps's own kazerne. */
  const korpsAdres = Boolean(
    gekozenKorps && isAdresVan(meldadres, gekozenKorps),
  );

  /**
   * Asked, not assumed: a dag on a borrowed terrein has an address the new korps cannot supply,
   * and rewriting it silently is how a boekje points at the wrong kazerne. Only asked where there
   * is something to lose - an empty meldadres just takes the korps's address over.
   */
  function kiesKorps(gekozenId: string) {
    const korps = korpsen.find((k) => k.id === gekozenId);

    setKorpsId(gekozenId);

    if (!overneembaarAdres(korps, meldadres)) {
      return;
    }

    if (ingevuld) {
      setTeBevestigen(korps);
    } else {
      neemAdresOver(korps);
    }
  }

  /**
   * Fills the fields and places the point in one act (#128) - unless the address is the korps's
   * own, where ADR-0030 makes it a non-override and PDOK's coordinate would route it separately.
   */
  function kiesAdres(treffer: GeocodeTreffer) {
    // Cleared rather than kept where PDOK's display name carried no postcode - some do not.
    // Whatever stood there belonged to the *previous* address, so keeping it would pair a new
    // street with an old postcode and print that pairing in the boekje. Empty is visibly empty,
    // and the field is `required` on the edit path, so the form says so before it saves.
    const gekozen = naarMeldadres(treffer);
    const isKorpsEigenAdres = Boolean(
      gekozenKorps && isAdresVan(gekozen, gekozenKorps),
    );

    setMeldadres(
      isKorpsEigenAdres
        ? { ...gekozen, latitude: null, longitude: null }
        : gekozen,
    );
    setHandmatig(false);
  }

  /** A typed edit, which is what makes the address an override rather than the korps's own. */
  function typAdres(veld: Partial<Meldadres>) {
    setMeldadres(metAdres(meldadres, veld));
    setHandmatig(true);
  }

  function neemAdresOver(korps: Korps) {
    // No point either: this is now the korps's own address, and a meldadres that is the korps's
    // address carries no coordinates at all - routes are measured to the korps's Portaal point.
    setMeldadres({
      address: korps.address ?? "",
      postalCode: korps.postalCode ?? "",
      city: korps.city,
      latitude: null,
      longitude: null,
    });
    setHandmatig(false);
    setTeBevestigen(null);
  }

  return (
    <>
      <form
        className="space-y-4"
        onSubmit={(event) => {
          event.preventDefault();
          onSubmit(
            naarDagWaarden({
              date,
              niveau,
              korpsId,
              ingevuld,
              meldadres,
              klasseIds,
              wedstrijdleider,
              telefoonnummer,
            }),
          );
        }}
      >
        <DatumNiveauVelden
          id={id}
          t={t}
          date={date}
          niveau={niveau}
          niveaus={niveaus}
          onDateChange={setDate}
          onNiveauChange={setNiveauId}
        />

        {/* A warning and not a rule: §3 lets the beheerder enter the dagen, so the save goes through. */}
        {waarschuwing && (
          <Alert tone="warning">
            {t("dagForm.buitenSeizoen", { naam: seizoen.name })}
          </Alert>
        )}

        <KorpsKiezer
          label={`${id}-korps`}
          korpsen={korpsen}
          gekozenId={korpsId || undefined}
          onKies={kiesKorps}
          onWis={() => {
            // The address went with the korps. Cleared here as well as on the server, so the
            // form stops showing an address it is no longer going to send.
            setKorpsId("");
            setMeldadres(leegMeldadres);
          }}
        />

        <MeldadresSectie
          id={id}
          t={t}
          korpsId={korpsId}
          gekozenKorps={gekozenKorps}
          korpsAdres={korpsAdres}
          meldadres={meldadres}
          handmatig={handmatig}
          vereist={Boolean(dag)}
          onKiesAdres={kiesAdres}
          onTypAdres={typAdres}
        />

        {nieuw && (
          <BanenFieldset
            id={id}
            t={t}
            klassen={klassen}
            klasseIds={klasseIds}
            klasse={klasse}
            onKiesKlasse={setTeVoegen}
            onVoegToe={(gekozenKlasse) =>
              setKlasseIds([...klasseIds, gekozenKlasse])
            }
            onVerwijder={(index) =>
              setKlasseIds(klasseIds.filter((_, positie) => positie !== index))
            }
          />
        )}

        {nieuw && (
          <WedstrijdleidersFieldset
            id={id}
            t={t}
            korpsId={korpsId}
            wedstrijdleider={wedstrijdleider}
            telefoonnummer={telefoonnummer}
            onWedstrijdleiderChange={setWedstrijdleider}
            onTelefoonnummerChange={setTelefoonnummer}
          />
        )}

        <FormActions>
          {error && <p className="text-sm text-destructive">{error.message}</p>}
          <Button type="submit" disabled={pending}>
            {pending ? t("common:actions.busy") : submitLabel}
          </Button>
        </FormActions>
      </form>

      <KorpsAdresBevestigDialog
        t={t}
        teBevestigen={teBevestigen}
        meldadres={meldadres}
        onOpenChange={(open) => !open && setTeBevestigen(null)}
        onBevestig={neemAdresOver}
      />
    </>
  );
}

/** Datum and niveau: the two fields every dag needs, regardless of the create or edit path. */
function DatumNiveauVelden({
  id,
  t,
  date,
  niveau,
  niveaus,
  onDateChange,
  onNiveauChange,
}: {
  id: string;
  t: TFunction<["wedstrijden", "common"]>;
  date: string;
  niveau: string;
  niveaus: ReturnType<typeof useNiveaus>["data"];
  onDateChange: (waarde: string) => void;
  onNiveauChange: (waarde: string) => void;
}) {
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <div className="grid gap-2">
        <Label htmlFor={`${id}-datum`}>{t("dagForm.datum")}</Label>
        <Input
          id={`${id}-datum`}
          type="date"
          value={date}
          onChange={(event) => onDateChange(event.target.value)}
          required
        />
      </div>
      <div className="grid gap-2">
        {/* The standard's own example of a control that may be a select: three niveaus, the
            same three every year, global rather than per seizoen (ADR-0029). */}
        <Label htmlFor={`${id}-niveau`}>{t("dagForm.niveau")}</Label>
        <Select
          id={`${id}-niveau`}
          value={niveau}
          onChange={(event) => onNiveauChange(event.target.value)}
        >
          {(niveaus ?? []).map((option) => (
            <option key={option.id} value={option.id}>
              {option.name}
            </option>
          ))}
        </Select>
      </div>
    </div>
  );
}

/**
 * A meldadres without a korps overrides nothing and the server refuses the pair; not rendered
 * rather than hidden, since a browser will not submit a form holding an unfocusable required
 * control. Still editable once a korps is chosen: a borrowed terrein has a meldadres the BAG
 * does not carry (ADR-0060).
 */
function MeldadresSectie({
  id,
  t,
  korpsId,
  gekozenKorps,
  korpsAdres,
  meldadres,
  handmatig,
  vereist,
  onKiesAdres,
  onTypAdres,
}: {
  id: string;
  t: TFunction<["wedstrijden", "common"]>;
  korpsId: string;
  gekozenKorps: Korps | undefined;
  korpsAdres: boolean;
  meldadres: Meldadres;
  handmatig: boolean;
  vereist: boolean;
  onKiesAdres: (treffer: GeocodeTreffer) => void;
  onTypAdres: (veld: Partial<Meldadres>) => void;
}) {
  if (!korpsId) {
    return <Alert>{t("dagForm.geenKorps")}</Alert>;
  }

  return (
    <fieldset className="grid gap-3">
      <FieldLegend>{t("dagForm.meldadres")}</FieldLegend>
      <p className="text-sm text-muted-foreground">
        {t("dagForm.meldadresUitleg")}
      </p>

      <MeldadresZoeker
        id={`${id}-adreszoeker`}
        meldadres={meldadres}
        korpsNaam={gekozenKorps?.name ?? ""}
        korpsAdres={korpsAdres}
        handmatig={handmatig}
        onKies={onKiesAdres}
      />

      <div className="grid gap-2">
        <Label htmlFor={`${id}-adres`}>{t("dagForm.adres")}</Label>
        <Input
          id={`${id}-adres`}
          value={meldadres.address}
          onChange={(event) => onTypAdres({ address: event.target.value })}
          required={vereist}
        />
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="grid gap-2">
          <Label htmlFor={`${id}-postcode`}>{t("dagForm.postcode")}</Label>
          <Input
            id={`${id}-postcode`}
            value={meldadres.postalCode}
            onChange={(event) => onTypAdres({ postalCode: event.target.value })}
            required={vereist}
          />
        </div>
        <div className="grid gap-2">
          <Label htmlFor={`${id}-plaats`}>{t("dagForm.plaats")}</Label>
          <Input
            id={`${id}-plaats`}
            value={meldadres.city}
            onChange={(event) => onTypAdres({ city: event.target.value })}
            required={vereist}
          />
        </div>
      </div>
    </fieldset>
  );
}

/**
 * One gekozen klasse as a removable chip; keyed on the position as well as the klasse, because a
 * repeat is meaningful here, and two Aspiranten LD chips are two banen rather than one drawn
 * twice. No confirmation, unlike the Banen card's: nothing is saved and nothing carries a number
 * yet, so no baan is lost and none is renamed.
 */
function BaanChip({
  naam,
  verwijderLabel,
  onVerwijder,
}: {
  naam: string;
  verwijderLabel: string;
  onVerwijder: () => void;
}) {
  return (
    <li className="flex items-center gap-1 rounded-md border bg-background py-1 pr-1 pl-3 text-sm">
      <span className="font-medium">{naam}</span>
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        aria-label={verwijderLabel}
        onClick={onVerwijder}
      >
        <X />
      </Button>
    </li>
  );
}

/**
 * Create path only: once the dag exists its Banen card does this, and confirms removals because
 * taking a baan out renumbers its klasse (ADR-0030).
 */
function BanenFieldset({
  id,
  t,
  klassen,
  klasseIds,
  klasse,
  onKiesKlasse,
  onVoegToe,
  onVerwijder,
}: {
  id: string;
  t: TFunction<["wedstrijden", "common"]>;
  klassen: ReturnType<typeof useKlassen>["data"];
  klasseIds: string[];
  klasse: string;
  onKiesKlasse: (klasseId: string) => void;
  onVoegToe: (klasseId: string) => void;
  onVerwijder: (index: number) => void;
}) {
  return (
    <fieldset className="grid gap-3">
      <FieldLegend>{t("dagForm.banen")}</FieldLegend>
      <p className="text-sm text-muted-foreground">
        {t("dagForm.banenUitleg")}
      </p>

      {klasseIds.length > 0 && (
        <ul
          aria-label={t("dagForm.banenLijstLabel")}
          className="flex flex-wrap gap-2"
        >
          {klasseIds.map((gekozenKlasse, index) => {
            const naam =
              klassen?.find((k) => k.id === gekozenKlasse)?.name ??
              t("dagForm.banenOnbekend");

            return (
              <BaanChip
                key={`${gekozenKlasse}-${index}`}
                naam={naam}
                verwijderLabel={t("dagForm.baanVerwijderKnop", { baan: naam })}
                onVerwijder={() => onVerwijder(index)}
              />
            );
          })}
        </ul>
      )}

      <div className="flex flex-wrap items-end gap-2">
        <div className="grid min-w-52 flex-1 gap-2">
          <Label htmlFor={`${id}-baan-klasse`}>{t("dagForm.baanKlasse")}</Label>
          <Select
            id={`${id}-baan-klasse`}
            value={klasse}
            onChange={(event) => onKiesKlasse(event.target.value)}
          >
            {(klassen ?? []).map((option) => (
              <option key={option.id} value={option.id}>
                {option.name}
              </option>
            ))}
          </Select>
        </div>
        {/* `type="button"`: a submit-typed control here would create the dag instead. */}
        <Button
          type="button"
          variant="secondary"
          disabled={!klasse}
          onClick={() => klasse && onVoegToe(klasse)}
        >
          <Plus />
          {t("dagForm.baanToevoegen")}
        </Button>
      </div>
    </fieldset>
  );
}

/**
 * The two boekje fields known when a dag is appointed; the other three stay on the
 * Boekjegegevens card, where all five are corrected (ADR-0030).
 */
function WedstrijdleidersFieldset({
  id,
  t,
  korpsId,
  wedstrijdleider,
  telefoonnummer,
  onWedstrijdleiderChange,
  onTelefoonnummerChange,
}: {
  id: string;
  t: TFunction<["wedstrijden", "common"]>;
  korpsId: string;
  wedstrijdleider: string;
  telefoonnummer: string;
  onWedstrijdleiderChange: (waarde: string) => void;
  onTelefoonnummerChange: (waarde: string) => void;
}) {
  return (
    <fieldset className="grid gap-3">
      <FieldLegend>{t("dagForm.wedstrijdleiders")}</FieldLegend>
      {/* Also what keeps the header apart from the near-identical label under it. */}
      <p className="text-sm text-muted-foreground">
        {t("dagForm.wedstrijdleidersUitleg")}
      </p>

      <Field>
        <FieldLabel htmlFor={`${id}-wedstrijdleider`}>
          {t("boekje.wedstrijdleider")}
        </FieldLabel>
        <Input
          id={`${id}-wedstrijdleider`}
          value={wedstrijdleider}
          maxLength={200}
          onChange={(event) => onWedstrijdleiderChange(event.target.value)}
        />
        {/* Ranking needs a point to route to, and there is none before a korps is chosen. */}
        {korpsId ? (
          <WedstrijdleiderPicker
            bron={{ soort: "korps", korpsId }}
            onKies={(naam, nummer) => {
              onWedstrijdleiderChange(naam);
              onTelefoonnummerChange(nummer ?? "");
            }}
          />
        ) : (
          <div className="flex items-center gap-2">
            <Button type="button" variant="outline" size="sm" disabled>
              {t("boekje.kiesWedstrijdleider")}
            </Button>
            <span className="text-xs text-muted-foreground">
              {t("dagForm.kiesWedstrijdleiderKorpsEerst")}
            </span>
          </div>
        )}
        <FieldDescription>{t("boekje.wedstrijdleiderUitleg")}</FieldDescription>
      </Field>

      <Field>
        <FieldLabel htmlFor={`${id}-telefoon`}>
          {t("boekje.telefoon")}
        </FieldLabel>
        <Input
          id={`${id}-telefoon`}
          value={telefoonnummer}
          maxLength={200}
          onChange={(event) => onTelefoonnummerChange(event.target.value)}
        />
        <FieldDescription>{t("boekje.telefoonUitleg")}</FieldDescription>
      </Field>
    </fieldset>
  );
}

/** Outside the form: Radix portals it anyway, and a nested submit would fire on Enter. */
function KorpsAdresBevestigDialog({
  t,
  teBevestigen,
  meldadres,
  onOpenChange,
  onBevestig,
}: {
  t: TFunction<["wedstrijden", "common"]>;
  teBevestigen: Korps | null;
  meldadres: Meldadres;
  onOpenChange: (open: boolean) => void;
  onBevestig: (korps: Korps) => void;
}) {
  return (
    <AlertDialog open={teBevestigen !== null} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t("dagForm.overnemenTitle")}</AlertDialogTitle>
          <AlertDialogDescription>
            {t("dagForm.overnemenDescription", {
              adres: meldadres.address,
              postcode: meldadres.postalCode,
              plaats: meldadres.city,
              korps: teBevestigen?.name ?? "",
            })}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{t("dagForm.overnemenLaten")}</AlertDialogCancel>
          <AlertDialogAction
            onClick={() => teBevestigen && onBevestig(teBevestigen)}
          >
            {t("dagForm.overnemenActie")}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/**
 * Compared loosely: Portaal and PDOK agree on the building, not the spelling, and an exact
 * comparison would store a point for what is the same address.
 */
function isAdresVan(meldadres: Meldadres, korps: Korps) {
  return (
    genormaliseerd(meldadres.address) === genormaliseerd(korps.address) &&
    genormaliseerd(meldadres.postalCode) === genormaliseerd(korps.postalCode) &&
    genormaliseerd(meldadres.city) === genormaliseerd(korps.city)
  );
}
